import { API } from "../config/axiosConfig";
import endpoints from "../endpoints/endpoints";

const buyerModule = {
  // `favourites/list/` reads `page` + `page_size`
  // (apps/api/apps/cards/views.py, `get_favourites`). It has never read
  // `index`/`offset`; sending those was silently dropped by Django's
  // `.get(name, default)` and the view's own default of 10 won every time.
  // That capped EVERY caller at ten rows: the collection page asking for 24,
  // the summary tile asking for 200, and — since this branch made the saved
  // list the single source of truth for "is this card favourited" — the
  // membership check itself, so a buyer with more than ten favourites was
  // told the older ones were not saved and could not un-favourite them.
  // An explicit `page_size` wins; `limit` is this app's legacy spelling.
  getSavedCards: async (params = { page: 1, limit: 5 }) => {
    try {
      const { page, limit, ...rest } = params;
      const response = await API.get("api/cards/favourites/list/", {
        params: {
          ...(page === undefined ? {} : { page }),
          ...(limit === undefined ? {} : { page_size: limit }),
          ...rest,
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch saved cards",
      };
    }
  },

  saveCard: async (card_lookup_id) => {
    try {
      const response = await API.post("api/cards/favourites/add/", {
        card_lookup_id,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        // `status` is surfaced because a caller has to be able to tell
        // "this id will never be accepted" (the endpoint answers 400 when the
        // card does not resolve) from "try again later" (offline, 5xx, an
        // expired token). The sign-in merge retries the second forever and
        // must not retry the first forever. Undefined when the request never
        // reached a server.
        status: error.response?.status,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to save card",
      };
    }
  },

  unsaveCard: async (card_lookup_id) => {
    try {
      const response = await API.delete("api/cards/favourites/remove/", {
        params: { card_lookup_id },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to unsave card",
      };
    }
  },

  getBuyerOrders: async (params = { page: 1, limit: 5 }) => {
    try {
      // `buyer-order-list` reads `page` + `page_size`
      // (apps/api/apps/dashboard/views.py:702), NOT `limit` — unlike
      // `get-card-listings` in the same Django app, which reads `limit`.
      // Sending `limit` is silently ignored and the view falls back to its
      // own default of 10, so a caller asking for 4 rows gets 10. Translate
      // here, the same way `getSavedCards` above translates `limit` into the
      // `page_size` that view really reads, so every call site gets the
      // endpoint's real vocabulary.
      // An explicit `page_size` wins; `limit` is the legacy spelling.
      const { limit, ...rest } = params;
      const response = await API.get("api/dashboard/buyer-order-list", {
        params:
          limit === undefined || rest.page_size !== undefined
            ? rest
            : { ...rest, page_size: limit },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch buyer orders",
      };
    }
  },

  /* Resolves favourite card IDS to live cards. Used by the collection page when
     the buyer is signed out and the only thing on hand is a list of ids. */
  getCardsByIds: async (ids = []) => {
    if (!ids.length) return { success: true, data: [], total: 0 };
    try {
      const response = await API.get(endpoints.getCardsByIds, {
        params: { ids: ids.join(',') },
      });
      return {
        success: true,
        data: response.data?.cards || [],
        total: response.data?.total || 0,
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        total: 0,
        error:
          error.response?.data?.message || error.message || 'Failed to load saved cards',
      };
    }
  },
};

export default buyerModule;
